import React from 'react'
import { useNavigate } from 'react-router-dom'

import { MkNavItem } from '@typescript'
import { ZinButton, ZinIcon } from '@tnl/zin-react'
import { AccountMenuItem } from '../AccountMenuItem'
import './MkAccountMenuItems.scss'

export interface MkAccountMenuItem extends Omit<MkNavItem, 'icon'> {
  key: string
  onClick?: (e: any) => void
  url?: string
  link?: string
  icon?: string | React.ReactNode
  children?: React.ReactNode
}

export interface MkAccountMenuItemsProps extends React.HTMLAttributes<HTMLUListElement> {
  items: MkAccountMenuItem[]
}

/**
 * MkAccountMenuItems component renders a list of account menu items.
 *
 * @param {MkAccountMenuItemsProps} props - The properties for the component.
 * @param {string} props.className - The CSS class name for the component.
 * @param {Array} props.items - The array of menu item configurations.
 *
 * @returns {JSX.Element} The rendered account menu items.
 *
 * @component
 * @example
 * const items = [
 *   { key: '1', label: 'Profile', url: '/profile', icon: 'user' },
 *   { key: '2', label: 'Settings', onClick: handleSettingsClick, icon: <SettingsIcon /> },
 *   { key: '3', label: '', children: <CustomComponent /> }
 * ];
 * return <MkAccountMenuItems className="account-menu" items={items} />;
 */
export const MkAccountMenuItems: React.FC<MkAccountMenuItemsProps> = ({ className, items }) => {
  const navigate = useNavigate()

  const renderAccountMenuItem = (label: string, onClick?: (e: any) => void, url?: string, icon?: string | React.ReactNode, children?: React.ReactNode) => {
    if (!url && !children && label === '') return <hr />

    // Use the new AccountMenuItem component for menu items with labels
    if (label) {
      return (
        <AccountMenuItem
          label={label}
          icon={icon}
          onClick={onClick ? (e: any) => onClick(e) : undefined}
          link={url}
        />
      )
    }

    // Fallback to ZinButton for items without labels (like separators)
    const dataTestId = label ? `mk-menu-account-${label.toLowerCase().replace(/\s+/g, '-')}` : 'mk-menu-account-separator'

    return (
      <ZinButton
        link={!!url}
        label={label}
        icon={icon && typeof icon === 'string' ? <ZinIcon name={icon} /> : icon}
        onClick={url && !onClick ? () => navigate(url || '#', '_self') : onClick}
        data-testid={dataTestId}>
        {children}
      </ZinButton>
    )
  }

  return (
    <ul className={className}>
      {items?.map((config) => <li key={config?.key}>{renderAccountMenuItem(config.label, config.onClick, config.url || config?.link, config.icon, config.children)}</li>)}
    </ul>
  )
}
